/**
 * Shared trigger button for a Popover-based searchable combobox — used by both
 * `SupplierPicker` (mapping-phase-parts.tsx) and `MappingFieldPicker`
 * (mapping-field-picker.tsx) so the trigger markup lives in one place instead of being
 * copy-pasted per combobox.
 */
import * as React from 'react';
import { ChevronsUpDown } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';

export interface ComboboxTriggerProps {
  open: boolean;
  label: string;
  testId?: string;
}

// Radix's `PopoverTrigger asChild` clones this element via Slot, injecting its own onClick
// and ref — both MUST reach the underlying <button>, so this forwards the ref and spreads any
// extra props (...rest) instead of only reading its own named props.
export const ComboboxTrigger = React.forwardRef<
  HTMLButtonElement,
  ComboboxTriggerProps & React.ComponentPropsWithoutRef<'button'>
>(({ open, label, testId, ...rest }, ref) => (
  <Button
    ref={ref}
    type="button"
    variant="outline"
    role="combobox"
    aria-expanded={open}
    className="w-full justify-between font-normal text-sm"
    data-testid={testId}
    {...rest}
  >
    <span className="truncate">{label}</span>
    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
  </Button>
));
ComboboxTrigger.displayName = 'ComboboxTrigger';
